import { Box, Checkbox, Chip, Fade, Grid, Typography } from "@mui/material";
import {
	DragDropContext,
	DragStart,
	Draggable,
	DropResult,
	Droppable,
} from "@hello-pangea/dnd";
import React, { FC, useCallback, useMemo, useRef, useState } from "react";

import CircularProgress from "@mui/material/CircularProgress";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { IDNDList } from "@websential/cosmic-v20";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";

/* ============================================================================
 * Public types
 * ==========================================================================*/

/** A single row rendered inside either column. Extra fields are passed through untouched. */
export interface IDualListItem {
	id: string;
	text: string;
	[key: string]: any;
}


/** Copy / labels for a single column - fully customizable per usage. */
export interface IDualListColumnConfig {
	title: string;
	emptyTitle?: string;
	emptySubtitle?: string;
}

/** Fired ONLY when item(s) cross from one column into the other - the right place to trigger an API call. */
export interface IMoveItemsPayload {
	sourceDroppableId: string;
	destinationDroppableId: string;
	movedItemIds: string[];
	movedItems: IDualListItem[];
	isBulk: boolean;
}

export interface IDragDropDDLPanelProps {
	firstList: IDNDList;
	secondList: IDNDList;
	firstListConfig: IDualListColumnConfig;
	secondListConfig: IDualListColumnConfig;

	/** Fired on cross-column moves only. Use this to call your API / mutation. */
	onMoveItems: (payload: IMoveItemsPayload) => void;
	/**
	 * Fired whenever the underlying lists change shape - both on same-column reorder
	 * and on cross-column move. Persist these back into your own state (e.g. via setState).
	 */
	onListsChange: (
		updatedFirstList: IDNDList,
		updatedSecondList: IDNDList
	) => void;

	/** Shows a small spinner in place of the count chip, and disables select-all, while true. */
	isLoading?: boolean;
	/** Accent color used for selection, borders, chips. Defaults to a teal (#0091b3). */
	accentColor?: string;
	/** Optional icon rendered in the little avatar circle next to each row's text. */
	itemIcon?: React.ReactNode;
	/** Turn off the select-all checkbox in the column headers if you don't want it. Defaults to true. */
	enableSelectAll?: boolean;
	/** Base test id, suffixed per-column and per-row for automated testing. */
	testId?: string;
}

/* ============================================================================
 * Internal helpers
 * ==========================================================================*/

const DEFAULT_ACCENT = "#0091b3";

const areAllSelected = (ids: string[], elements: IDualListItem[]) =>
	elements.length > 0 && elements.every((el) => ids.includes(el.id));

const someSelected = (ids: string[], elements: IDualListItem[]) =>
	elements.some((el) => ids.includes(el.id));

/* ============================================================================
 * Component
 * ==========================================================================*/

export const DragDropDDLPanel: FC<IDragDropDDLPanelProps> = ({
	firstList,
	secondList,
	firstListConfig,
	secondListConfig,
	onMoveItems,
	onListsChange,
	isLoading = false,
	accentColor = DEFAULT_ACCENT,
	itemIcon,
	enableSelectAll = true,
	testId = "dual-selectable-drag-list",
}) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [selectedListId, setSelectedListId] = useState<string | null>(null);
	const lastSelectedIndexRef = useRef<number | null>(null);

	const getListById = useCallback(
		(droppableId: string) =>
			droppableId === firstList.droppableId ? firstList : secondList,
		[firstList, secondList]
	);

	const clearSelection = useCallback(() => {
		setSelectedIds([]);
		setSelectedListId(null);
		lastSelectedIndexRef.current = null;
	}, []);

	/* ---------- Row selection (click / shift-click / cross-column reset) ---------- */

	const onItemClick = useCallback(
		(e: React.MouseEvent, droppableId: string, itemId: string, index: number) => {
			const isRangeKey = e.shiftKey;

			// Clicking in a different column than the current selection starts a fresh selection there -
			// only one column's selection is "active" at a time, since only one action can be performed.
			if (selectedListId && selectedListId !== droppableId) {
				setSelectedIds([itemId]);
				setSelectedListId(droppableId);
				lastSelectedIndexRef.current = index;
				return;
			}

			if (isRangeKey && lastSelectedIndexRef.current !== null) {
				const list = getListById(droppableId);
				const start = Math.min(lastSelectedIndexRef.current, index);
				const end = Math.max(lastSelectedIndexRef.current, index);
				const rangeIds = list.elements.slice(start, end + 1).map((el) => el.id);
				setSelectedIds((prev) => Array.from(new Set([...prev, ...rangeIds])));
				setSelectedListId(droppableId);
				return;
			}

			// Plain click toggles the item in/out of the selection.
			setSelectedIds((prev) =>
				prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
			);
			setSelectedListId(droppableId);
			lastSelectedIndexRef.current = index;
		},
		[selectedListId, getListById]
	);

	/* ---------- Select-all / clear-all per column ---------- */

	const onToggleSelectAll = useCallback(
		(list: IDNDList) => {
			const isCurrentlyAllSelected =
				selectedListId === list.droppableId && areAllSelected(selectedIds, list.elements);

			if (isCurrentlyAllSelected) {
				clearSelection();
				return;
			}

			const allIds = list.elements.map((el) => el.id);
			setSelectedIds(allIds);
			setSelectedListId(list.droppableId);
			lastSelectedIndexRef.current = allIds.length - 1;
		},
		[selectedIds, selectedListId, clearSelection]
	);

	/* ---------- Drag start: dragging an unselected row re-anchors selection to it ---------- */

	const onDragStart = useCallback(
		(start: DragStart) => {
			const { draggableId, source } = start;
			if (!selectedIds.includes(draggableId)) {
				setSelectedIds([draggableId]);
				setSelectedListId(source.droppableId);
			}
		},
		[selectedIds]
	);

	/* ---------- Drag end: reorder locally, or move across columns (+ notify parent) ---------- */

	const onDragEnd = useCallback(
		(result: DropResult) => {
			const { source, destination, draggableId } = result;
			if (!destination) return;

			const sameList = source.droppableId === destination.droppableId;
			if (sameList && source.index === destination.index) return;

			const isMulti = selectedIds.includes(draggableId) && selectedIds.length > 1;
			const idsToMove = isMulti ? selectedIds : [draggableId];

			if (sameList) {
				// --- Reorder within the same column - no move event fired ---
				const list = getListById(source.droppableId);
				const remaining = list.elements.filter((el) => !idsToMove.includes(el.id));
				const movedItems = list.elements.filter((el) => idsToMove.includes(el.id));
				const insertAt = Math.min(destination.index, remaining.length);
				const updatedElements = [
					...remaining.slice(0, insertAt),
					...movedItems,
					...remaining.slice(insertAt),
				];

				const updatedList = { ...list, elements: updatedElements };
				if (list.droppableId === firstList.droppableId) {
					onListsChange(updatedList, secondList);
				} else {
					onListsChange(firstList, updatedList);
				}
			} else {
				// --- Cross-column move - the only case that should trigger an API call ---
				const sourceList = getListById(source.droppableId);
				const destinationList = getListById(destination.droppableId);

				const movedItems = sourceList.elements.filter((el) => idsToMove.includes(el.id));
				const updatedSourceElements = sourceList.elements.filter(
					(el) => !idsToMove.includes(el.id)
				);
				const updatedDestinationElements = [...destinationList.elements];
				updatedDestinationElements.splice(destination.index, 0, ...movedItems);

				const updatedSourceList = { ...sourceList, elements: updatedSourceElements };
				const updatedDestinationList = { ...destinationList, elements: updatedDestinationElements };

				if (sourceList.droppableId === firstList.droppableId) {
					onListsChange(updatedSourceList, updatedDestinationList);
				} else {
					onListsChange(updatedDestinationList, updatedSourceList);
				}

				onMoveItems({
					sourceDroppableId: source.droppableId,
					destinationDroppableId: destination.droppableId,
					movedItemIds: movedItems.map((el) => el.id),
					movedItems,
					isBulk: movedItems.length > 1,
				});
			}

			clearSelection();
		},
		[firstList, secondList, selectedIds, getListById, onListsChange, onMoveItems, clearSelection]
	);

	/* ---------- Rendering ---------- */

	const renderEmptyState = (config: IDualListColumnConfig) => (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				height: "100%",
				minHeight: "260px",
				color: "#a3a7b7",
				gap: 1,
			}}
		>
			<InboxRoundedIcon sx={{ fontSize: 34, opacity: 0.5 }} />
			<Typography variant="body2" sx={{ fontWeight: 500 }}>
				{config.emptyTitle ?? "Nothing here"}
			</Typography>
			{config.emptySubtitle && (
				<Typography variant="caption" sx={{ opacity: 0.8 }}>
					{config.emptySubtitle}
				</Typography>
			)}
		</Box>
	);

	const renderColumn = (list: IDNDList, config: IDualListColumnConfig) => {
		const selectedInThisList = selectedListId === list.droppableId ? selectedIds : [];
		const allSelected = areAllSelected(selectedInThisList, list.elements);
		const partiallySelected = !allSelected && someSelected(selectedInThisList, list.elements);

		return (
			<Grid size={{ xs: 12, md: 6 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 1.5,
					}}
				>
					<Typography
						variant="subtitle1"
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							fontWeight: 600,
							letterSpacing: 0.2,
							color: "#20232b",
						}}
					>
						{config.title}
						{isLoading ? (
							<CircularProgress
								sx={{
									width: "20px!important",
									height: "20px!important",
									"& .MuiCircularProgress-svg": { width: "20px", height: "20px" },
								}}
							/>
						) : (
							<Chip
								label={list.elements.length}
								size="small"
								sx={{
									height: 20,
									fontSize: "0.72rem",
									fontWeight: 700,
									bgcolor: `${accentColor}14`,
									color: accentColor,
								}}
							/>
						)}
					</Typography>

				</Box>
				{enableSelectAll && (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "end", gap: 0.25 }}>
						<Typography variant="caption" sx={{ color: "#a3a7b7", fontWeight: 500 }}>
							Select all
						</Typography>
						<Checkbox
							size="small"
							checked={allSelected && list.elements.length > 0}
							indeterminate={partiallySelected}
							disabled={isLoading || list.elements.length === 0}
							onChange={() => onToggleSelectAll(list)}
							data-testid={`${testId}-select-all-${list.droppableId}`}
							sx={{
								p: 0.25,
								color: "#c7cbe0",
								"&.Mui-checked": { color: accentColor },
								"&.MuiCheckbox-indeterminate": { color: accentColor },
							}}
						/>
					</Box>
				)}

				<Droppable droppableId={list.droppableId}>
					{(provided, droppableSnapshot) => (
						<Box
							ref={provided.innerRef}
							{...provided.droppableProps}
							data-testid={`${testId}-column-${list.droppableId}`}
							sx={{
								minHeight: "320px",
								maxHeight: "420px",
								overflowY: "auto",
								borderRadius: 3,
								p: 1.25,
								border: "1.5px solid",
								borderColor: droppableSnapshot.isDraggingOver ? accentColor : "#e6e8f0",
								bgcolor: droppableSnapshot.isDraggingOver ? `${accentColor}0A` : "#fafbfc",
								transition:
									"border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease",
								boxShadow: droppableSnapshot.isDraggingOver
									? `inset 0 0 0 1px ${accentColor}33`
									: "none",
								"&::-webkit-scrollbar": { width: 6 },
								"&::-webkit-scrollbar-thumb": { bgcolor: "#d7dae3", borderRadius: 3 },
							}}
						>
							{list.elements.length === 0 && !droppableSnapshot.isDraggingOver
								? renderEmptyState(config)
								: list.elements.map((item, index) => {
									const isSelected = selectedListId === list.droppableId
										? selectedIds.includes(item.id)
										: false;
									return (
										<Draggable key={item.id} draggableId={item.id} index={index}>
											{(dragProvided, dragSnapshot) => (
												<Box
													ref={dragProvided.innerRef}
													{...dragProvided.draggableProps}
													{...dragProvided.dragHandleProps}
													onClick={(e) => onItemClick(e, list.droppableId, item.id, index)}
													data-testid={`${testId}-item-${item.id}`}
													sx={{
														position: "relative",
														display: "flex",
														alignItems: "center",
														gap: 1,
														mb: 1,
														px: 1.25,
														py: 1,
														borderRadius: 2.5,
														cursor: dragSnapshot.isDragging ? "grabbing" : "grab",
														userSelect: "none",
														bgcolor: isSelected ? accentColor : "#fff",
														color: isSelected ? "#fff" : "#2a2d38",
														border: "1px solid",
														borderColor: isSelected
															? accentColor
															: dragSnapshot.isDragging
																? accentColor
																: "#e9ebf1",
														boxShadow: dragSnapshot.isDragging
															? "0 12px 24px -8px rgba(91,95,239,0.35), 0 2px 6px rgba(0,0,0,0.08)"
															: isSelected
																? "0 1px 4px rgba(91,95,239,0.25)"
																: "0 1px 2px rgba(16,24,40,0.04)",
														transform: dragSnapshot.isDragging
															? "scale(1.03) rotate(-1deg)"
															: "scale(1)",
														transition: dragSnapshot.isDragging
															? "none" // no transition while actively dragging - must track cursor 1:1
															: "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
														"&:hover": {
															borderColor: isSelected ? accentColor : "#c7cbe0",
															boxShadow: dragSnapshot.isDragging
																? undefined
																: "0 2px 8px rgba(16,24,40,0.08)",
														},
													}}
												>
													<DragIndicatorRoundedIcon
														sx={{ fontSize: 18, opacity: isSelected ? 0.85 : 0.35, flexShrink: 0 }}
													/>
													{itemIcon && (
														<Box
															sx={{
																width: 26,
																height: 26,
																borderRadius: "50%",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																flexShrink: 0,
																bgcolor: isSelected ? "rgba(255,255,255,0.2)" : `${accentColor}12`,
																color: isSelected ? "#fff" : accentColor,
															}}
														>
															{itemIcon}
														</Box>
													)}
													<Typography
														variant="body2"
														sx={{
															fontWeight: isSelected ? 600 : 500,
															flexGrow: 1,
															whiteSpace: "nowrap",
															overflow: "hidden",
															textOverflow: "ellipsis",
														}}
													>
														{item.text}
													</Typography>

													{dragSnapshot.isDragging && isSelected && selectedIds.length > 1 && (
														<Box
															sx={{
																position: "absolute",
																top: -8,
																right: -8,
																bgcolor: "#e14444",
																color: "#fff",
																borderRadius: "50%",
																width: 22,
																height: 22,
																fontSize: "0.7rem",
																fontWeight: 700,
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																boxShadow: "0 2px 6px rgba(225,68,68,0.4)",
															}}
														>
															{selectedIds.length}
														</Box>
													)}
												</Box>
											)}
										</Draggable>
									);
								})}
							{provided.placeholder}
						</Box>
					)}
				</Droppable>
			</Grid>
		);
	};

	return (
		<Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
			{selectedIds.length > 0 && (
				<Fade in>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.5,
							mb: 2,
							px: 1.5,
							py: 0.75,
							borderRadius: 2,
							bgcolor: `${accentColor}0F`,
							width: "fit-content",
						}}
					>
						<Typography variant="caption" sx={{ color: accentColor, fontWeight: 600 }}>
							{selectedIds.length} item(s) selected
						</Typography>
						<Typography
							variant="caption"
							onClick={clearSelection}
							sx={{
								color: accentColor,
								fontWeight: 600,
								textDecoration: "underline",
								cursor: "pointer",
								opacity: 0.75,
								"&:hover": { opacity: 1 },
							}}
						>
							Clear
						</Typography>
					</Box>
				</Fade>
			)}

			<DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
				<Grid container spacing={3}>
					{renderColumn(firstList, firstListConfig)}
					{renderColumn(secondList, secondListConfig)}
				</Grid>
			</DragDropContext>
		</Box>
	);
};

export default DragDropDDLPanel;